import type { Dinero as DineroObj } from 'dinero.js'
import Dinero from 'dinero.js'
import { v4 as uuid4 } from 'uuid'

const currency = 'EUR'
const zero = toMoney(0)

type Discount =
  | {
      type: 'percentage'
      amount: number
      freeDelivery?: boolean
      promotionId?: string
      concept?: string
    }
  | {
      type: 'currency'
      amount: number
      freeDelivery?: boolean
      promotionId?: string
      concept?: string
    }
  | {
      type: '2x1'
      amount: number
      freeDelivery?: boolean
      promotionId?: string
      concept?: string
    }
  | null
  | undefined

type TaxRate = {
  value: number
  label?: string
  delivery?: number
  takeaway?: number
  category?: string
}

type DeliveryOrder = {
  deliveryFee?: number
  minimumBasketSurcharge?: number
  customerName?: string
  address?: string
  addressDetails?: string
  postalCode?: string
  customerPhoneNumber?: string
  needCutlery?: boolean
  preferredPaymentMethod?: string
}

type Modifier = {
  name: string
  priceImpact: number
  parentModifierId?: string
  quantity: number
}

type ComboProductModifier = Modifier & {
  parentModifierId?: string
}

type ComboProduct = {
  id: string
  name: string
  priceImpact: number
  quantity: number
  modifiers?: ComboProductModifier[]
}

type TabProduct = {
  id: string
  parentProduct?: string
  name: string
  quantity: number
  price: number
  fullPrice?: number
  finalPrice: number
  discountType?: string | null
  discountAmount?: number | null
  discountConcept?: string | null
  promotionId?: string
  modifiers: Modifier[]
  comboProducts?: ComboProduct[]
  comments?: string
  taxes?: TaxRate
}

type BillProductModifier = {
  id: string
  name: string
  priceImpact: number
  catalogModifierId?: string
  quantity: number
}

type BillComboProductModifier = {
  id: string
  name: string
  priceImpact: number
  tabComboProductModifierId?: string
  quantity: number
}

type BillComboProduct = {
  id: string
  tabComboProductId: string
  name: string
  priceImpact: number
  quantity: number
  modifiers?: BillComboProductModifier[]
}

type BillProduct = {
  tabProductId: string
  catalogProductId?: string
  id: string
  name: string
  quantity: number
  price: number
  originalPrice: number
  discountType?: string | null
  discountAmount?: number | null
  discountConcept?: string | null
  promotionId?: string | null
  modifiers: BillProductModifier[]
  comboProducts: BillComboProduct[]
  comments?: string
  taxes: TaxRate
}

type Payment = {
  amount: number
  tip?: number
}

type Tab = {
  id?: string
  seats?: number | any[]
  shared?: any[]
  source?: string
  products?: { quantity: number }[]
  deliveryOrder?: DeliveryOrder
  terraceSurchargePercentage?: number
  pickupType?: string
  customerCompany?: any
  allergyInfo?: any
  customerInfo?: {
    name?: string
    surname?: string
    phoneNumber?: string
    phoneNumberCode?: string
  }
  tableName?: string
  name?: string
  virtualBrandId?: string
  code?: string
  customerNote?: string
  schedulingTime?: string | Date | null
  activationTime?: string | Date | null
  promotionId?: string | null
}

type TaxCategory = {
  originalTotal: number
  total: number
  discountTotal: number
  terraceSurcharge: number
  taxableBase: number
  tax: number
  taxPercentage: number
}

type Bill = {
  id: string
  tabId: string
  creationTime: string | Date
  number: string | null
  payments: any[]
  type: 'total' | 'with-tips' | 'products'
  title: string
  products: BillProduct[]
  total: number
  tax: number
  taxPercentage: number
  taxableBase: number
  taxLabel: string
  originalTotal: number
  discountTotal: number
  deliveryFee: number
  minimumBasketSurcharge: number
  terraceSurcharge: number
  terraceSurchargePercentage: number
  taxRates: TaxCategory[]
  company?: any
  diners?: number
  customerCompany?: any
  allergyInfo?: any
  discount?: Discount
  customerName?: string
  customerAddress?: string
  customerAddressDetails?: string
  customerPostalCode?: string
  customerPhoneNumber?: string
  needCutlery?: boolean
  preferredPaymentMethod?: string
  customerPhoneNumberCode?: string
  tableName?: string
  source?: string
  virtualBrandId?: string
  code?: string
  note?: string
  pickupType?: string
  schedulingTime?: string | Date | null
  activationTime?: string | Date | null
  locationBrandId?: string
  promotionId?: string
  ticketInfo?: any
  paid?: number
  pending?: number
}

function toMoney(amount: number): DineroObj {
  return Dinero({ amount, currency })
}

function sum(p1: DineroObj, p2: DineroObj): DineroObj {
  return p1.add(p2)
}

function addProductsPrice(products: { price: number }[]): DineroObj {
  return products.map(p => toMoney(p.price)).reduce(sum, zero)
}

function calculateDiscountAndSurcharge(
  products: { price: number }[],
  discount: Discount,
  terraceSurchargePercentage: number
): { discountTotal: DineroObj; terraceSurcharge: DineroObj } {
  const productsTotal = addProductsPrice(products)
  let discountTotal = zero
  if (discount) {
    switch (discount.type) {
      case 'percentage':
        discountTotal = productsTotal.percentage(discount.amount)
        break
      case 'currency':
        discountTotal = toMoney(discount.amount)
        break
    }
  }
  const terraceSurcharge = productsTotal
    .subtract(discountTotal)
    .percentage(terraceSurchargePercentage)

  return {
    discountTotal,
    terraceSurcharge
  }
}

function mapTaxCategories(
  products: BillProduct[],
  taxRate: TaxRate,
  discount: Discount,
  deliveryOrder: DeliveryOrder | undefined,
  terraceSurchargePercentage: number,
  pickupType: string | undefined
): TaxCategory[] {
  let extrasTotal = zero
  let deliveryFee = zero
  let minimumBasketSurcharge = zero
  const { taxRateType, defaultTaxPercentage } = getTaxRateTypeAndDefaultTaxPercentage(
    pickupType,
    taxRate
  )
  const { discountTotal, terraceSurcharge } = calculateDiscountAndSurcharge(
    products,
    discount,
    terraceSurchargePercentage
  )
  if (deliveryOrder) {
    if (!discount?.freeDelivery) {
      deliveryFee = toMoney(deliveryOrder.deliveryFee || 0)
    }
    minimumBasketSurcharge = toMoney(deliveryOrder.minimumBasketSurcharge || 0)
    extrasTotal = extrasTotal.add(deliveryFee).add(minimumBasketSurcharge)
  }
  const groupedTaxCategories = products.reduce(
    (taxCategories, product) => {
      const taxKey = product.taxes && taxRateType ? product.taxes[taxRateType] : undefined
      const taxCategory = taxKey !== undefined ? taxCategories[taxKey] || 0 : 0
      if (taxKey !== undefined) {
        taxCategories[taxKey] = taxCategory + product.price
      }
      return taxCategories
    },
    {} as Record<string, number>
  )
  if (groupedTaxCategories && Object.keys(groupedTaxCategories).length === 0) {
    return []
  }

  const emptyTaxes = Object.keys(groupedTaxCategories).filter(
    taxCategory => groupedTaxCategories[taxCategory] <= 0
  )
  emptyTaxes.forEach(emptyTax => {
    delete groupedTaxCategories[emptyTax]
  })

  if (extrasTotal.getAmount() > 0) {
    if (groupedTaxCategories[defaultTaxPercentage]) {
      groupedTaxCategories[defaultTaxPercentage] += extrasTotal.getAmount()
    } else {
      groupedTaxCategories[defaultTaxPercentage] = extrasTotal.getAmount()
    }
  }
  let weightedDiscount: DineroObj[] = []
  let weightedTerraceSurcharge: DineroObj[] = []

  if (Object.keys(groupedTaxCategories).length > 0) {
    weightedDiscount = discountTotal.allocate(
      Object.keys(groupedTaxCategories).map(taxPercentage => groupedTaxCategories[taxPercentage])
    )
    weightedTerraceSurcharge = terraceSurcharge.allocate(
      Object.keys(groupedTaxCategories).map(taxPercentage => groupedTaxCategories[taxPercentage])
    )
  }

  return Object.keys(groupedTaxCategories).map((taxPercentage, i) => {
    const originalTotal = groupedTaxCategories[taxPercentage]
    const taxPerc = parseFloat((Number(taxPercentage) * 100).toFixed(2))
    let total = toMoney(originalTotal)
      .subtract(weightedDiscount[i])
      .add(weightedTerraceSurcharge[i])
    total = Dinero.maximum([total, toMoney(0)])
    const taxableBase = total
      .multiply(100)
      .divide(100 + taxPerc)
      .getAmount()
    return {
      originalTotal,
      total: total.getAmount(),
      discountTotal: weightedDiscount[i].getAmount(),
      terraceSurcharge: weightedTerraceSurcharge[i].getAmount(),
      taxableBase,
      tax: total.getAmount() - taxableBase,
      taxPercentage: taxPerc
    }
  })
}

function addPaymentsPrice(payments: Payment[]): DineroObj {
  return payments.map(payment => toMoney(payment.amount - (payment.tip || 0))).reduce(sum, zero)
}

function breakdown(
  mappedProducts: BillProduct[],
  discount: Discount,
  deliveryOrder: DeliveryOrder | undefined,
  terraceSurchargePercentage: number,
  taxRate: TaxRate,
  pickupType?: string
): Omit<
  Bill,
  'id' | 'tabId' | 'creationTime' | 'number' | 'payments' | 'type' | 'title' | 'products'
> {
  const taxCategories = mapTaxCategories(
    mappedProducts,
    taxRate,
    discount,
    deliveryOrder,
    terraceSurchargePercentage,
    pickupType
  )
  const groupedBreakdown = taxCategories.reduce(
    (taxes, taxCategory) => {
      taxes.total += taxCategory.total
      taxes.discountTotal += taxCategory.discountTotal
      taxes.taxableBase += taxCategory.taxableBase
      taxes.tax += taxCategory.tax
      taxes.originalTotal += taxCategory.originalTotal
      taxes.terraceSurcharge += taxCategory.terraceSurcharge
      return taxes
    },
    {
      total: 0,
      discountTotal: 0,
      taxableBase: 0,
      tax: 0,
      originalTotal: 0,
      terraceSurcharge: 0
    }
  )
  const deliveryFee = discount?.freeDelivery ? 0 : deliveryOrder?.deliveryFee || 0
  return {
    total: groupedBreakdown.total,
    tax: groupedBreakdown.tax,
    taxPercentage: taxRate.value * 100,
    taxableBase: groupedBreakdown.taxableBase,
    taxLabel: taxRate.label!,
    originalTotal: groupedBreakdown.originalTotal,
    discountTotal: groupedBreakdown.discountTotal,
    deliveryFee: deliveryFee,
    minimumBasketSurcharge: deliveryOrder?.minimumBasketSurcharge || 0,
    terraceSurcharge: groupedBreakdown.terraceSurcharge || 0,
    terraceSurchargePercentage: terraceSurchargePercentage,
    taxRates: taxCategories
  }
}

function mapModifiers(modifiers: Modifier[] = []): BillProductModifier[] {
  return modifiers.map(modifier => ({
    id: uuid4(),
    name: modifier.name,
    priceImpact: modifier.priceImpact,
    catalogModifierId: modifier.parentModifierId,
    quantity: modifier.quantity
  }))
}

function mapComboProductModifiers(
  modifiers: ComboProductModifier[] = []
): BillComboProductModifier[] {
  return modifiers.map(modifier => ({
    id: uuid4(),
    name: modifier.name,
    priceImpact: modifier.priceImpact,
    tabComboProductModifierId: modifier.parentModifierId,
    quantity: modifier.quantity
  }))
}

function mapProducts(products: TabProduct[], taxRate: TaxRate): BillProduct[] {
  return compute2x1Products(products)
    .filter(product => product.quantity > 0)
    .map(product => {
      const price = product.finalPrice
      return {
        tabProductId: product.id,
        catalogProductId: product.parentProduct,
        id: uuid4(),
        name: product.name,
        quantity: product.quantity,
        price: Math.round(price * product.quantity),
        originalPrice: Math.round(product.fullPrice! * product.quantity),
        discountType: product.discountType,
        discountAmount: product.discountAmount,
        discountConcept: product.discountConcept,
        promotionId: product.promotionId,
        modifiers: mapModifiers(product.modifiers),
        comboProducts: (product.comboProducts || []).map(comboProduct => ({
          id: uuid4(),
          tabComboProductId: comboProduct.id,
          name: comboProduct.name,
          priceImpact: comboProduct.priceImpact,
          quantity: comboProduct.quantity,
          modifiers: mapComboProductModifiers(comboProduct.modifiers || [])
        })),
        comments: product.comments,
        taxes: product.taxes || taxRate
      }
    })
}

function compute2x1Products(products: TabProduct[]): TabProduct[] {
  return products.flatMap(product => {
    let mappedProducts: TabProduct[] = []
    if (product.discountType === '2x1') {
      mappedProducts = [
        {
          ...product,
          quantity: product.discountAmount!,
          finalPrice: 0,
          discountType: '2x1',
          discountAmount: null
        },
        {
          ...product,
          discountType: null,
          discountAmount: null,
          quantity: product.quantity - product.discountAmount!
        }
      ]
    } else {
      mappedProducts = [product]
    }
    return mappedProducts
  })
}

function cloneBill(bill: Bill): Bill {
  return {
    ...bill,
    id: uuid4(),
    creationTime: new Date(),
    products: bill.products.map(product => ({
      ...product,
      id: uuid4(),
      modifiers: product.modifiers.map((modifier: any) => ({
        ...modifier,
        id: uuid4()
      })),
      comboProducts: (product.comboProducts || []).map((comboProduct: any) => ({
        ...comboProduct,
        id: uuid4(),
        modifiers: (comboProduct.modifiers || []).map((modifier: any) => ({
          ...modifier,
          id: uuid4()
        }))
      }))
    }))
  }
}

function getTaxRateTypeAndDefaultTaxPercentage(
  pickupType: string | undefined,
  taxRate: TaxRate | undefined
): { taxRateType: keyof TaxRate; defaultTaxPercentage: number } {
  let taxRateType: keyof TaxRate
  let defaultTaxPercentage: number
  if (!taxRate) {
    return { taxRateType: 'value', defaultTaxPercentage: 10 }
  }
  switch (pickupType) {
    case 'delivery':
    case 'ownDelivery':
      taxRateType = 'delivery'
      defaultTaxPercentage = taxRate.delivery!
      break
    case 'takeAway':
      taxRateType = 'takeaway'
      defaultTaxPercentage = taxRate.takeaway!
      break
    default:
      taxRateType = 'value'
      defaultTaxPercentage = taxRate.value
  }
  return { taxRateType, defaultTaxPercentage }
}

export default {
  generateProductsBill({
    products,
    company = {},
    tab = {},
    discount,
    taxRate,
    ticketInfo
  }: {
    products: TabProduct[]
    company?: any
    tab?: Tab
    discount?: Discount
    taxRate: TaxRate
    ticketInfo?: any
  }): Bill {
    const mappedProducts = mapProducts(products, taxRate)
    let diners = 0
    if (Array.isArray(tab.seats)) {
      diners = tab.seats.length
    } else if (tab.seats) {
      diners = tab.seats as number
    }
    const productsBilled = products ? products.length : 0
    let tabProducts = 0
    if (tab.source) {
      if (tab.source === 'Restaurant') {
        if (Array.isArray(tab.shared)) {
          tabProducts += tab.shared.length
        }
        if (Array.isArray(tab.seats)) {
          tabProducts += tab.seats.reduce((acc: number, seat: any) => acc + seat.length || 0, 0)
        }
      } else if (tab.products) {
        tabProducts = tab.products.reduce((acc, product) => acc + product.quantity, 0)
      }
    }
    const type = productsBilled === tabProducts ? 'total' : 'products'
    const bill: Bill = {
      id: uuid4(),
      tabId: tab.id!,
      creationTime: new Date(),
      number: null,
      payments: [],
      type,
      title: 'Products', // FIXME: remove from DB
      products: mappedProducts,
      ...breakdown(
        mappedProducts,
        discount,
        tab.deliveryOrder,
        tab.terraceSurchargePercentage || 0,
        taxRate,
        tab.pickupType
      ),
      company,
      diners,
      customerCompany: tab.customerCompany,
      allergyInfo: tab.allergyInfo,
      discount,
      ...(tab.deliveryOrder && {
        customerName: tab.deliveryOrder.customerName,
        customerAddress: tab.deliveryOrder.address,
        customerAddressDetails: tab.deliveryOrder.addressDetails,
        customerPostalCode: tab.deliveryOrder.postalCode,
        customerPhoneNumber: tab.deliveryOrder.customerPhoneNumber,
        needCutlery: tab.deliveryOrder.needCutlery,
        preferredPaymentMethod: tab.deliveryOrder.preferredPaymentMethod
      }),
      ...(tab.customerInfo && {
        customerName: [tab.customerInfo.name, tab.customerInfo.surname]
          .filter(name => name)
          .join(' '),
        customerPhoneNumber: tab.customerInfo.phoneNumber,
        customerPhoneNumberCode: tab.customerInfo.phoneNumberCode
      }),
      tableName: tab.tableName || tab.name,
      source: tab.source,
      virtualBrandId: tab.virtualBrandId,
      code: tab.code,
      note: tab.customerNote,
      pickupType: tab.pickupType,
      schedulingTime: tab.schedulingTime,
      activationTime: tab.activationTime,
      locationBrandId: tab.virtualBrandId,
      promotionId: tab.promotionId || discount?.promotionId,
      ticketInfo
    }
    return bill
  },
  addPaymentInfo<T extends { total: number }>(
    bill: T,
    payments: Payment[]
  ): T & { paid: number; pending: number } {
    const paid = addPaymentsPrice(payments).getAmount()
    const pending = Math.max(0, bill.total - paid)
    return {
      ...bill,
      paid: paid,
      pending: pending
    }
  },
  generateInvoice(bill: Bill, customerCompany: any): Bill {
    const invoice = cloneBill(bill)
    invoice.customerCompany = customerCompany
    invoice.number = null
    return invoice
  },
  getTaxRateTypeAndDefaultTaxPercentage
}
